"use client";

import * as React from "react";

/**
 * Tauri WebView ships with a custom UA tag `JobutupakiMeetDesktop/X.Y.Z`.
 * On mount we check /downloads/latest.json and compare the version. If a
 * newer version is published, render a small dismissable banner that
 * opens the installer URL in the system browser.
 *
 * No-op on non-desktop browsers.
 */
const DISMISS_KEY_PREFIX = "jobutu.update.dismissed.";
const UA_RE = /JobutupakiMeetDesktop\/(\d+)\.(\d+)\.(\d+)/;

function parseSemver(s: string): [number, number, number] | null {
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(s);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function cmpSemver(a: [number, number, number], b: [number, number, number]): number {
  for (let i = 0; i < 3; i++) {
    if (a[i]! !== b[i]!) return a[i]! - b[i]!;
  }
  return 0;
}

type Platform = "win64" | "macarm64";

function detectPlatform(): Platform | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  if (/Windows/.test(ua)) return "win64";
  if (/Mac OS X/.test(ua) || /Macintosh/.test(ua)) return "macarm64";
  return null;
}

export function DesktopUpdateBanner() {
  const [info, setInfo] = React.useState<{
    current: string;
    latest: string;
    url: string;
  } | null>(null);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const ua = navigator.userAgent;
    const m = UA_RE.exec(ua);
    if (!m) return; // not the desktop build
    const current = `${m[1]}.${m[2]}.${m[3]}`;
    const cur = parseSemver(current);
    if (!cur) return;

    let cancelled = false;
    fetch("/downloads/latest.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: null | { version: string; platforms: Record<string, { url: string }> }) => {
        if (cancelled || !data) return;
        const latest = parseSemver(data.version);
        if (!latest) return;
        if (cmpSemver(latest, cur) <= 0) return; // already up to date
        const platform = detectPlatform();
        if (!platform) return;
        const url = data.platforms?.[platform]?.url;
        if (!url) return;
        const dismissedFor = window.localStorage.getItem(DISMISS_KEY_PREFIX + data.version);
        if (dismissedFor) return;
        setInfo({ current, latest: data.version, url });
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  if (!info) return null;

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 16,
        transform: "translateX(-50%)",
        zIndex: 90,
        background: "linear-gradient(135deg, #5b8def 0%, #7c5bef 100%)",
        color: "#fff",
        padding: "10px 14px",
        borderRadius: 12,
        boxShadow: "0 12px 30px -8px rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        gap: 14,
        fontSize: 13,
        maxWidth: "92vw",
      }}
    >
      <span>
        Desktop update available — <b>v{info.latest}</b> (you have v{info.current})
      </span>
      <a
        href={info.url}
        style={{
          background: "rgba(0,0,0,0.25)",
          color: "#fff",
          padding: "6px 12px",
          borderRadius: 8,
          fontWeight: 600,
          textDecoration: "none",
        }}
        // The Tauri shell already punts http(s) downloads to the
        // system browser via should_punt_to_system_browser, so a
        // plain <a> is enough.
      >
        Download
      </a>
      <button
        type="button"
        onClick={() => {
          window.localStorage.setItem(DISMISS_KEY_PREFIX + info.latest, "1");
          setInfo(null);
        }}
        aria-label="Dismiss update notice"
        style={{
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.4)",
          color: "#fff",
          padding: "4px 8px",
          borderRadius: 6,
          cursor: "pointer",
          fontSize: 12,
        }}
      >
        Later
      </button>
    </div>
  );
}
